import { useSelection, SelectAll, SelectOne, BulkBar } from "@/components/core/BulkActions";
import { Copy, Pencil, Trash2, Images, Megaphone } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { fmtDateTime } from "@/lib/api";
import { formatoLabel } from "@/lib/ped";
import { EmptyState } from "@/components/core/AppLayout";
import { ChannelIcons, PostStatoSelect } from "./PedBits";
import { useAttachmentCounts } from "@/components/core/AttachmentsPanel";
import { CommentBadge } from "@/components/core/CommentsThread";

export const PedList = ({ posts, onEdit, onDuplicate, onDelete, onStatus, onBulkDone, emptyAction }) => {
  const attCounts = useAttachmentCounts("post", [posts?.length]);
  const selection = useSelection();
  if (!posts?.length) return <EmptyState icon={Megaphone} title="Nessun post" text="Crea il primo post del piano editoriale oppure genera idee con l'AI." action={emptyAction} testid="ped-list-empty" />;
  return (
    <>
    <BulkBar tipo="post" selection={selection} onDone={() => onBulkDone?.()} />
    <Table data-testid="ped-list">
      <TableHeader>
        <TableRow className="hover:bg-transparent">
          <TableHead className="w-10"><SelectAll ids={posts.map((p) => p.id)} selection={selection} testid="ped-select-all" /></TableHead>
          {["Pubblicazione", "Canali", "Formato", "Post", "Socio", "Stato", ""].map((h) => <TableHead key={h} className="text-xs font-bold uppercase tracking-wider">{h}</TableHead>)}
        </TableRow>
      </TableHeader>
      <TableBody>
        {posts.map((p) => (
          <TableRow key={p.id} className="group" data-testid={`ped-row-${p.id}`}>
            <TableCell><SelectOne id={p.id} selection={selection} testid={`ped-select-${p.id}`} /></TableCell>
            <TableCell className="text-sm font-semibold text-slate-700 whitespace-nowrap">{fmtDateTime(p.data_pubblicazione)}</TableCell>
            <TableCell><ChannelIcons canali={p.canali} size={16} /></TableCell>
            <TableCell className="text-sm">{formatoLabel(p.formato)}</TableCell>
            <TableCell className="max-w-[340px]">
              <button onClick={() => onEdit(p)} className="text-left font-bold text-slate-900 hover:text-[#E8173A] inline-flex items-center gap-2" data-testid={`ped-row-open-${p.id}`}>{p.titolo}<CommentBadge n={p.commenti} testid={`ped-row-comments-${p.id}`} />{attCounts[p.id] > 0 && <span className="rounded-full bg-[#0A1128] px-2 py-0.5 text-[10px] font-bold text-white" data-testid={`ped-row-att-${p.id}`}>📎 {attCounts[p.id]}</span>}
                {p.modifiche_richieste && <span className="text-[10px] font-bold uppercase tracking-wider rounded-full bg-[#E8173A] text-white px-2 py-0.5" data-testid={`ped-row-changes-${p.id}`}>Modifiche richieste</span>}</button>
              <div className="text-xs text-slate-500 truncate flex items-center gap-1.5">
                {p.media.length > 0 && <span className="inline-flex items-center gap-0.5 text-[#0A1128] font-bold"><Images className="h-3 w-3" />{p.media.length}</span>}
                {p.testo || "Nessun testo"}
              </div>
            </TableCell>
            <TableCell className="text-sm">{p.socio_nome || "—"}</TableCell>
            <TableCell><PostStatoSelect post={p} onChange={onStatus} /></TableCell>
            <TableCell>
              <div className="flex gap-1 opacity-60 group-hover:opacity-100 transition-opacity">
                <button onClick={() => onDuplicate(p)} title="Duplica" className="p-2 rounded-lg hover:bg-[#A6DAFC] text-[#0A1128]" data-testid={`ped-duplicate-${p.id}`}><Copy className="h-4 w-4" /></button>
                <button onClick={() => onEdit(p)} title="Modifica" className="p-2 rounded-lg hover:bg-[#A6DAFC] text-[#0A1128]" data-testid={`ped-edit-${p.id}`}><Pencil className="h-4 w-4" /></button>
                <button onClick={() => onDelete(p)} title="Elimina" className="p-2 rounded-lg hover:bg-red-50 text-[#E8173A]" data-testid={`ped-delete-${p.id}`}><Trash2 className="h-4 w-4" /></button>
              </div>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
    </>
  );
};
